import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { View, Smartphone, X, QrCode, Camera, Box, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";

// Wood stain colors for the 3D model
const WOOD_STAINS_HEX = {
  antique_stripped_pine: "#E8D4A8",
  golden_pine: "#D4A84B",
  dark_pine: "#8B6914",
  brown_mahogany: "#5C3317",
  very_red_mahogany: "#8B2500",
  dark_red_mahogany: "#5C1A0B",
  light_oak: "#C9A86C",
  golden_oak: "#B8860B",
  medium_oak: "#A0785A",
  french_oak: "#8B7355",
  english_oak: "#6B4423",
  dark_walnut: "#4A3728",
  light_rosewood: "#9C6B5C",
  light_sycamore: "#F5DEB3",
  black_black: "#1A1A1A",
  black_ebony: "#2D2D2D",
  ironstone_grey: "#4A4A4A",
  driftwood_grey: "#78736E",
  moonlight_grey: "#B0B0B0",
  dovetail_grey: "#6E6E6E",
  soft_white: "#F5F5F0",
  police_box_blue: "#003B6F",
  christmas_green: "#165B33",
  christmas_red: "#B31B1B",
  natural: "#E3C28D",
};

const ARViewButton = ({ config }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modelLoaded, setModelLoaded] = useState(false);
  const modelViewerRef = useRef(null);
  
  const stainHex = WOOD_STAINS_HEX[config?.stain_color] || WOOD_STAINS_HEX.natural;

  // Load model-viewer script when modal opens
  useEffect(() => {
    if (isModalOpen && !window.customElements?.get('model-viewer')) {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.3.0/model-viewer.min.js';
      script.onload = () => setModelLoaded(true);
      document.head.appendChild(script);
    } else if (isModalOpen) {
      setModelLoaded(true);
    }
  }, [isModalOpen]);

  // Generate model URL from backend API
  const getModelUrl = () => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    // Check if this is a PVC enclosure
    if (config?.isPVC || config?.type === 'pvc') {
      const params = new URLSearchParams({
        width: config?.width?.toString() || '4',
        height: config?.height?.toString() || '3',
        depth: config?.depth?.toString() || '2',
        pvc_color: config?.pvc_color || config?.stain_color || 'white'
      });
      return `${baseUrl}/api/generate-pvc-model?${params.toString()}`;
    }
    
    // Standard wood enclosure
    const params = new URLSearchParams({
      shape: config?.shape || 'rectangular',
      width: config?.width?.toString() || '4',
      height: config?.height?.toString() || '3',
      depth: config?.depth?.toString() || '2',
      stain_color: config?.stain_color || 'natural'
    });
    return `${baseUrl}/api/generate-model?${params.toString()}`;
  };

  const getDimensionsText = () => {
    return `${config?.width || 4}ft × ${config?.height || 3}ft × ${config?.depth || 2}ft`;
  };

  const getDimensionsMetric = () => {
    const ftToM = 0.3048;
    const w = config?.width || 4;
    const h = config?.height || 3;
    const d = config?.depth || 2;
    return `${(w * ftToM).toFixed(2)}m × ${(h * ftToM).toFixed(2)}m × ${(d * ftToM).toFixed(2)}m`;
  };

  const handleOpenModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const handleCloseModal = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsModalOpen(false);
  };

  // Render modal using portal to avoid overflow clipping issues
  const renderModal = () => {
    if (!isModalOpen) return null;
    
    return createPortal(
      <div 
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
        onClick={handleCloseModal}
      >
          <div 
            className="bg-[#121212] border border-white/10 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <View className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#F4F1DE]">Augmented Reality Preview</h2>
                  <p className="text-sm text-[#F4F1DE]/60">See your enclosure in your space</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-[#F4F1DE]/60" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Enclosure Summary */}
              <div className="bg-[#0A0A0A] rounded-lg p-4 mb-6">
                <div className="flex items-start gap-4">
                  {/* Color Preview */}
                  <div 
                    className="w-16 h-16 rounded-lg flex-shrink-0"
                    style={{ 
                      backgroundColor: stainHex,
                      backgroundImage: `repeating-linear-gradient(
                        90deg,
                        transparent,
                        transparent 2px,
                        rgba(0,0,0,0.15) 2px,
                        rgba(0,0,0,0.15) 4px
                      )`
                    }}
                  />
                  <div className="flex-grow">
                    <h3 className="text-[#F4F1DE] font-medium capitalize">
                      {config?.shape || 'Rectangular'} Enclosure
                    </h3>
                    <p className="text-[#F4F1DE]/60 text-sm mt-1">
                      {getDimensionsText()} ({getDimensionsMetric()})
                    </p>
                    <p className="text-[#F4F1DE]/50 text-xs mt-1 capitalize">
                      {(config?.stain_color || 'natural').replace(/_/g, ' ')} finish
                    </p>
                  </div>
                </div>
              </div>

              {/* 3D Preview */}
              <div className="bg-[#0A0A0A] rounded-lg p-4 relative overflow-hidden" style={{ height: '350px' }}>
                {modelLoaded ? (
                  <model-viewer
                    ref={modelViewerRef}
                    src={getModelUrl()}
                    ar
                    ar-modes="webxr scene-viewer quick-look"
                    camera-controls
                    touch-action="pan-y"
                    auto-rotate
                    shadow-intensity="1"
                    environment-image="neutral"
                    exposure="0.8"
                    camera-orbit="45deg 55deg 2.5m"
                    min-camera-orbit="auto auto 1m"
                    max-camera-orbit="auto auto 5m"
                    style={{ width: '100%', height: '100%', backgroundColor: '#0A0A0A' }}
                  >
                    <button 
                      slot="ar-button"
                      className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors"
                    >
                      <Camera className="w-5 h-5" />
                      View in Your Room
                    </button>
                  </model-viewer>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                      <p className="text-[#F4F1DE]/60 text-sm">Loading 3D viewer...</p>
                    </div>
                  </div>
                )}
                
                {/* Overlay instructions */}
                <div className="absolute top-4 left-4 right-4 bg-black/60 backdrop-blur rounded-lg p-3 pointer-events-none">
                  <p className="text-[#F4F1DE]/80 text-xs text-center">
                    Drag to rotate • Pinch to zoom • Tap AR button to place in room
                  </p>
                </div>
                
                {/* Model info badge */}
                <div className="absolute bottom-4 right-4 bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded pointer-events-none">
                  ✓ Custom model generated
                </div>
              </div>

              {/* Instructions */}
              <div className="mt-6 bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Smartphone className="w-5 h-5 text-purple-400 mt-0.5" />
                  <div>
                    <p className="text-[#F4F1DE] font-medium text-sm">How to use AR</p>
                    <ol className="text-[#F4F1DE]/60 text-sm mt-2 space-y-1 list-decimal list-inside">
                      <li>Tap "View in Your Room" below the 3D preview</li>
                      <li>Point your camera at the floor where you want to place it</li>
                      <li>Move your phone to scan the area</li>
                      <li>Tap to place the enclosure</li>
                      <li>Walk around to see it from all angles!</li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* Size Reference */}
              <div className="mt-4 bg-[#0A0A0A] rounded-lg p-4">
                <h4 className="text-[#F4F1DE] font-medium mb-3 flex items-center gap-2">
                  <Box className="w-4 h-4 text-[#E07A5F]" />
                  Size Reference
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#F4F1DE]/50">Width</p>
                    <p className="text-[#F4F1DE]">{config?.width || 4}ft ({((config?.width || 4) * 30.48).toFixed(0)}cm)</p>
                  </div>
                  <div>
                    <p className="text-[#F4F1DE]/50">Height</p>
                    <p className="text-[#F4F1DE]">{config?.height || 3}ft ({((config?.height || 3) * 30.48).toFixed(0)}cm)</p>
                  </div>
                  <div>
                    <p className="text-[#F4F1DE]/50">Depth</p>
                    <p className="text-[#F4F1DE]">{config?.depth || 2}ft ({((config?.depth || 2) * 30.48).toFixed(0)}cm)</p>
                  </div>
                  <div>
                    <p className="text-[#F4F1DE]/50">Shape</p>
                    <p className="text-[#F4F1DE] capitalize">{config?.shape || 'Rectangular'}</p>
                  </div>
                </div>
              </div>

              {/* Tips */}
              <div className="mt-4 pt-4 border-t border-white/10">
                <h4 className="text-[#F4F1DE] font-medium mb-3">Tips for best AR experience</h4>
                <ul className="text-[#F4F1DE]/60 text-sm space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    Ensure good lighting in the room
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    Point at a flat surface (floor) to place the enclosure
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    Move slowly to help your device map the space
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    Stand back to see the full enclosure in context
                  </li>
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-white/10 flex justify-end gap-3">
              <Button
                type="button"
                onClick={handleCloseModal}
                variant="outline"
                className="border-white/10 text-[#F4F1DE]/60 hover:bg-white/5"
              >
                Close
              </Button>
            </div>
          </div>
        </div>,
      document.body
    );
  };

  return (
    <>
      {/* AR Button */}
      <button
        type="button"
        onClick={handleOpenModal}
        className="flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white py-4 px-4 rounded-md font-medium w-full h-full transition-all"
        data-testid="ar-view-button"
      >
        <View className="w-5 h-5" />
        <span>View in AR</span>
      </button>

      {/* Render modal via portal */}
      {renderModal()}
    </>
  );
};

export default ARViewButton;
